import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Form,
  FormActions,
  FormError,
  FormField,
  Modal,
  ModalBody,
  ModalHeader,
  Textarea,
  useZodForm,
} from '../ui';
import type { FormActionDescriptor } from '../ui';
import { AssigneePicker } from './AssigneePicker';
import {
  AssignInspectionBodySchema,
  assignInspection,
  inspectionsKeys,
  type InspectionLifecycle,
} from '../../lib/api/inspections.gen';
import { eligibleAssigneesKey } from '../../lib/inspectionAssignees';
import { errorMessage } from '../../lib/errorMessage';
import { useState } from 'react';

// The reassignment affordance (DOD §1.10 — every mutating endpoint exposed to a
// human ships its UI control in the same story; P05 §8; W4 row 2).
//
// ── Why this is not the lifecycle bar's generic prose prompt ──────────────
// `InspectionLifecycleBar` already knows how to ask for the one missing input a
// transition needs (a cancellation reason, a review note) — but `assign` needs
// TWO, and one of them is a PERSON, resolved from a server-filtered endpoint
// that only exists for this purpose. A textarea cannot ask that question, and a
// free-text name would be exactly the guess W4's server-filtered picker exists
// to make impossible. So the assign edge gets its own dialog and everything else
// keeps the generic one.
//
// ── The schema (G-26) ─────────────────────────────────────────────────────
// `AssignInspectionBodySchema.pick(...)` — the GENERATED contract schema, not a
// `z.object` written next to the form. `reason` is `min(1)` on the wire because
// P05 §8 requires it, so the resolver refuses an empty one BEFORE the round trip
// and the server refuses it again after; one schema, two enforcement points,
// no drift. `expectedRevision` is picked OUT because the page holds it — it is
// concurrency state, not something a person types.

export interface ReassignInspectionDialogProps {
  inspectionId: string;
  /** The row as it stands — for `expectedRevision` and the incumbent. */
  lifecycle: InspectionLifecycle;
  onClose: () => void;
  /** Called with the fresh projection so the caller can redraw without refetching. */
  onAssigned: (next: InspectionLifecycle) => void;
}

const schema = AssignInspectionBodySchema.pick({ assignedTo: true, reason: true });

export function ReassignInspectionDialog({
  inspectionId,
  lifecycle,
  onClose,
  onAssigned,
}: ReassignInspectionDialogProps): JSX.Element {
  const queryClient = useQueryClient();
  const [failure, setFailure] = useState<string | null>(null);
  const reassigning = Boolean(lifecycle.assignedTo);

  const form = useZodForm(schema, { defaultValues: { assignedTo: '', reason: '' } });
  const assignedTo = form.watch('assignedTo');

  const mutation = useMutation({
    mutationFn: (values: { assignedTo: string; reason: string }) =>
      assignInspection(inspectionId, { ...values, expectedRevision: lifecycle.revision }),
    onSuccess: async (response) => {
      queryClient.setQueryData(inspectionsKeys.detail(inspectionId), response.data);
      // The picker carries each person's open-job count, which this assignment
      // just changed for two of them.
      await queryClient.invalidateQueries({ queryKey: eligibleAssigneesKey({ inspectionId }) });
      onAssigned(response.data);
      onClose();
    },
    // G-27: whatever the server said — the scope refusal, the stale-revision
    // 409, the plan wall — rendered verbatim through the one unwrap point.
    onError: (err: unknown) => setFailure(errorMessage(err)),
  });

  return (
    <Modal onClose={onClose} label={reassigning ? 'Reassign inspection' : 'Assign inspection'}>
      {/* No ModalFooter: `FormSubmit` is owned by this dialog's own `<Form>`
          subtree (react-hook-form's `FormProvider` context does not extend
          past it), so the primary action stays inside `ModalBody` alongside
          the form rather than being lifted into a sibling footer region. */}
      <ModalHeader title={reassigning ? 'Reassign inspection' : 'Assign inspection'} onClose={onClose} />
      <ModalBody>
      <div className="space-y-4" data-testid="reassign-dialog">
        <Form
          form={form}
          onSubmit={(values) => {
            setFailure(null);
            mutation.mutate({ assignedTo: values.assignedTo, reason: values.reason });
          }}
          className="space-y-4"
        >
          <FormField name="assignedTo" label="Who should carry this out?" required control="group">
            {() => (
              <AssigneePicker
                inspectionId={inspectionId}
                value={assignedTo || null}
                onChange={(userId) =>
                  form.setValue('assignedTo', userId ?? '', { shouldValidate: true, shouldDirty: true })
                }
              />
            )}
          </FormField>
          <FormField
            name="reason"
            label="Why is it changing hands?"
            description={
              reassigning
                ? 'Both people are told once — the person losing the work reads this sentence.'
                : 'Recorded on the inspection, and shown to whoever is doing the work.'
            }
            required
          >
            {(field) => <Textarea {...field} rows={3} maxLength={4000} data-testid="reassign-reason-input" />}
          </FormField>
          <FormError message={failure} />
          <FormActions
            actions={
              [
                { id: 'cancel', type: 'button', label: 'Never mind', variant: 'ghost', onClick: onClose, 'data-testid': 'reassign-cancel' },
                {
                  id: 'submit',
                  type: 'submit',
                  label: reassigning ? 'Reassign' : 'Assign',
                  disabled: mutation.isPending,
                  'data-testid': 'reassign-submit',
                },
              ] satisfies FormActionDescriptor[]
            }
          />
        </Form>
      </div>
      </ModalBody>
    </Modal>
  );
}
